import React, { FC, useState, useRef, useEffect, useCallback, } from 'react'
import { useDispatch, } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { RangePickerProps } from 'antd/es/date-picker'
import { Space, Card, DatePicker, Select, Button } from 'antd'
import PageBreadcrumb from '@/components/pageBreadcrumb'
// 引入 echarts 核心模块
import * as echarts from 'echarts/core'
import { LegendComponent, GridComponent, TooltipComponent, TitleComponent, DataZoomComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
// 按需引入图表类型
import { BarChart, LineChart, PieChart } from 'echarts/charts'
import type { EventValue, RangeValue } from 'rc-picker/lib/interface'
import { barChart1Option } from '@/helpers/barChart'
import type { ILegendData } from '@/pages/charts'
import type { Moment } from 'moment'
import moment from 'moment'
import { cloneDeep } from 'lodash'
import { getContributeDateRange, getContribute } from '@/services/rag'
import Nav from '@/components/nav'

echarts.use([
    BarChart, LineChart, PieChart, CanvasRenderer,
    LegendComponent, GridComponent, TooltipComponent, TitleComponent, DataZoomComponent
])

const ContributionDegree: FC = () => {
    const history = useHistory()
    const dispatch = useDispatch()
    const { RangePicker } = DatePicker
    const { t, i18n } = useTranslation()
    const chartRef1 = useRef<any>(null)
    const chartInstance1 = useRef<any>(null)
    const chartRef2 = useRef<any>(null)
    const chartInstance2 = useRef<any>(null)
    const chartRef3 = useRef<any>(null)
    const chartInstance3 = useRef<any>(null)
    const [filterState, setFilterState] = useState('')
    const allData = useRef<any[]>([])
    const [winW, setWinW] = useState(0)
    const [selectPerson, setSelectPerson] = useState<string>(t('un select person'))
    const [routes, ] = useState([
        {
            path: '/indexPage',
            breadcrumbName: t('indexPage'),
        },
        {
            path: '/contribution',
            breadcrumbName: t('contribution degree statistics'),
        },
    ])
    const dateFormat = 'YYYY-MM'
    const momentObj = moment()
    const [minDate, setMinDate] = useState<Moment | null>(null)
    const [maxDate, setMaxDate] = useState<Moment | null>(null)
    const [dateRange, setDateRange] = useState<[EventValue<Moment>, EventValue<Moment>]>(
        [moment().subtract(12, 'month'), momentObj]
    )
    const [selectOption, setSelectOption] = useState<{
        label: string
        value: string
    }[]>([])

    const disabledDate: RangePickerProps['disabledDate'] = useCallback((current) => {
        return current.isBefore(minDate, 'month') || current.isAfter(maxDate, 'month')
    }, [minDate, maxDate])

    const handleChange = (str: string) => {
        const value = JSON.parse(str)
        console.log('value: ', value)
        setSelectPerson(value.nickName)
        setChart2(value.creationPhaseContribution)
        setChart3(value.reviewPhaseContribution)
    }

    const onChangeDate = (dates: RangeValue<Moment>) => {
        if (dates) {
            setDateRange(dates)
        } else {
            setDateRange([null, null])
        }
    }

    const getInitDateRange = async () => {
        const resData = await getContributeDateRange()
        setMinDate(moment(resData.minDate))
        setMaxDate(moment(resData.maxDate))
    }

    const setChart2 = (creationPhaseContribution: {
        techDefReview: number
        techDelReview: number
        techPlanReview: number
    }) => {
        const { techDefReview, techDelReview, techPlanReview } = creationPhaseContribution
        const chart = chartRef2.current
        if (chart) {
            const option = {
                title: {
                    subtext: t('creation phase') + (i18n.language !== 'zh' ? ' ' : '')
                        + t('contribution degree')
                },
                tooltip: {
                    trigger: 'axis'
                },
                xAxis: {
                    type: 'category',
                    data: [t('tech plan review'), t('tech def review'), t('technicalAchievementDelivery')],
                    axisLabel: {
                        rotate: 15
                    }
                },
                yAxis: {
                    type: 'value'
                },
                color: '#188df0',
                series: {
                    data: [Number(techPlanReview.toFixed(2)),
                        Number(techDefReview.toFixed(2)), Number(techDelReview.toFixed(2)),],
                    type: 'bar',
                    barMaxWidth: 80,
                    barMinHeight: 5,
                    label: {
                        show: true
                    }
                }
            }
            let chartInstance = chartInstance2.current
            if (!chartInstance) {
                chartInstance2.current = echarts.init(chart)
                chartInstance = chartInstance2.current
            } else {
                chartInstance.clear()
            }
            chartInstance.setOption(option)
        }    
    }
    const setChart3 = (reviewPhaseContribution: {
        attendMeeting: number
        audit: number
        comment: number
        relevantConfirm: number
    }) => {
        const { attendMeeting, audit, comment, relevantConfirm } = reviewPhaseContribution
        const chart = chartRef3.current
        if (chart) {
            const option = {
                title: {
                    subtext: t('review phase') + (i18n.language !== 'zh' ? ' ' : '')
                        + t('contribution degree')
                },
                tooltip: {
                    trigger: 'axis'
                },
                xAxis: {
                    type: 'category',
                    data: [t('relevant confirm'), t('attend meeting'), t('sitInOn'), t('comment')],
                    axisLabel: {
                        rotate: 15
                    }
                },
                yAxis: {
                    type: 'value'
                },
                color: '#188df0',
                series: {
                    data: [Number(relevantConfirm.toFixed(2)), Number(attendMeeting.toFixed(2)),
                        Number(audit.toFixed(2)), Number(comment.toFixed(2)),],
                    type: 'bar',
                    barMaxWidth: 80,
                    barMinHeight: 5,
                    label: {
                        show: true
                    }
                }
            }
            let chartInstance = chartInstance3.current
            if (!chartInstance) {
                chartInstance3.current = echarts.init(chart)
                chartInstance = chartInstance3.current
            } else {
                chartInstance.clear()
            }
            chartInstance.setOption(option)
        }
    }
    const initChart = async () => {
        const { memberContributionLelvelList }: {
            memberContributionLelvelList: {
                nickName: string
                totalContribution: number
                creationPhaseContribution: {
                    techDefReview: number
                    techDelReview: number
                    techPlanReview: number
                }
                reviewPhaseContribution: {
                    attendMeeting: number
                    audit: number
                    comment: number
                    relevantConfirm: number
                }
            }[]
        } = await getContribute({
            beginDate: dateRange[0]?.format('YYYY-MM') ?? '',
            endDate: dateRange[1]?.format('YYYY-MM') ?? '',
        })
        const chart = chartRef1.current
        if (chart) {
            let chartInstance = chartInstance1.current
            if (!chartInstance) {
                chartInstance1.current = echarts.init(chart)
                chartInstance = chartInstance1.current
            } else {
                chartInstance.clear()
            }
            
            console.log('memberContributionLelvelList: ', memberContributionLelvelList)
            allData.current = memberContributionLelvelList
            setSelectOption(memberContributionLelvelList.map(item => ({
                label: item.nickName,
                value: JSON.stringify(item)
            })))
            const xAxis: string[] = memberContributionLelvelList.map(item => item.nickName)
            const series: ILegendData[] = [{
                type: 'bar',
                name: 'count',
                data: memberContributionLelvelList.map(item => Number(item.totalContribution.toFixed(2))),
                barMaxWidth: 60,
                barMinHeight: 5,
                label: {
                    show: true
                }
            }]
            const option = cloneDeep(barChart1Option)
            // option.legend.data = []
            option.xAxis.data = xAxis
            option.series = series
            chartInstance.setOption({
                ...option,
                grid: {
                    ...option.grid,
                    left: 50,
                    right: 10,
                    top: 30,
                    bottom: 80
                },
                title: {
                    show: false
                },
                dataZoom: [{
                    show: true,
                    start: 0,
                    end: 20 / memberContributionLelvelList.length * 100,
                    brushSelect: false,
                    dataBackground: {
                        areaStyle: {
                            color: 'blue',
                        }
                    },
                    selectedDataBackground: {
                        lineStyle: {
                            color: 'green',
                            opacity: 1
                        },
                        areaStyle: {
                            color: '#5470c6',
                            opacity: 1
                        }
                    },
                    handleStyle: {
                        color: '#efb12b',
                        opacity: 1
                    },
                    emphasis: {
                        handleStyle: {
                            color: 'blue',
                        }
                    }
                }],
                legend: {
                    ...option.legend,
                    top: 0,
                    bottom: 'auto',
                    textStyle: {
                        color: '#333'
                    }
                },
                xAxis: {
                    ...option.xAxis,
                    axisLabel: {
                        ...option.xAxis.axisLabel,
                        fontSize: 10,
                        rotate: 15,
                        width: 150,
                        color: '#333'
                    }
                },
                yAxis: {
                    ...option.yAxis,
                    splitNumber: 4,
                    axisLabel: {
                        color: '#333'
                    }
                }
            })
            chartInstance.on('click', function(params: {
                name: string,
                data: number
                dataIndex: number
                seriesName: string
            }) {
                setSelectPerson(params.name)
                const findNameObj = memberContributionLelvelList.find(item => item.nickName === params.name)
                if (findNameObj) {
                    setChart2(findNameObj.creationPhaseContribution)
                    setChart3(findNameObj.reviewPhaseContribution)
                }
            })
        }
    }

    const setFilterStateFn = (str: string) => {
        let arr = allData.current.slice()
        const chartInstance = chartInstance1.current
        const options = chartInstance.getOption()
        if (filterState === str) {
            setFilterState('')
            options.dataZoom[0].end = 20 / arr.length * 100
        } else {
            setFilterState(str)
            if (str === 'top') {
                arr = arr.slice(0, 20)
                options.dataZoom[0].end = 100
            } else {
                const len = arr.length
                arr = arr.slice(len - 20)
                arr.reverse()
                options.dataZoom[0].end = 100
            }
        }
        console.log('options: ', options, arr)
        const xAxis: string[] = arr.map(item => item.nickName)
        options.xAxis[0].data = xAxis
        options.series[0].data = arr.map(item => Number(item.totalContribution.toFixed(2)))

        chartInstance.clear()
        chartInstance.setOption(options)
    }

    useEffect(() => {
        getInitDateRange()

        setWinW(document.body.clientWidth)
        window.onresize = () => {
            setWinW(document.body.clientWidth)
        }
        return () => {
            window.onresize = null
        }
    }, [])
    useEffect(() => {
        if (chartInstance1.current) {
            chartInstance1.current.resize()
        }
        if (chartInstance2.current) {
            chartInstance2.current.resize()
        }
        if (chartInstance3.current) {
            chartInstance3.current.resize()
        }
    }, [winW])
    useEffect(() => {
        if (dateRange[0] !== null && dateRange[1] !== null) {
            initChart()
        }
    }, [dateRange])

    return (<>
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Nav dispatch={dispatch} />
            <PageBreadcrumb routerParam={routes}
                history={history}
                dispatch={dispatch}
            />
            <Space style={{ width: '100%' }}
                direction='vertical'
            >
                <div className='index_ls'>
                    <Card size="small"
                        style={{ marginBottom: 10 }}
                        title={<div
                            style={{ display: 'flex', alignItems: 'center', gap: 10 }}
                            title={`${t('contribution degree')}`}
                        >
                            {t('contribution degree')}
                            <Button type='primary' ghost={ filterState !== 'top' } onClick={() => setFilterStateFn('top')}>Top 20</Button>
                            <Button type='primary' ghost={ filterState !== 'last' } onClick={() => setFilterStateFn('last')}>Last 20</Button>
                        </div>}
                        extra={<div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <RangePicker
                                style={{ width: 180 }}
                                picker="month"
                                format={dateFormat}
                                value={dateRange}
                                disabledDate={disabledDate}
                                onChange={onChangeDate}
                            />
                        </div>}
                    >
                        <div ref={chartRef1} style={{ height: 300 }} />
                    </Card>
                </div>
                <div className='index_ls'>
                    <Card size="small"
                        style={{ marginBottom: 10 }}
                        title={<Space>
                            {selectPerson}
                            <Select
                                value={selectPerson !== t('un select person') ? selectPerson : undefined}
                                placeholder={t('personSearch')}
                                showSearch
                                style={{ width: 220 }}
                                onChange={handleChange}
                                options={selectOption}
                            />
                        </Space>}
                    >
                        <div style={{ display: 'flex', width: '100%' }}>
                            <div ref={chartRef2} style={{ marginRight: 20, width: (winW - 50) * 0.45, height: 300 }} />
                            <div ref={chartRef3} style={{ flex: 1, height: 300 }} />
                        </div>
                    </Card>
                </div>
            </Space>
        </Space>
    </>)
}
export default ContributionDegree